import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Icon, IconName } from "../ui";
import { RootState } from "../store";
import { clearToken } from "../store/slices/authSlice";
import { logoutUser } from "../services/api";

type NavGroup = "overview" | "catalog" | "operations";

type NavItem = {
  id: string;
  label: string;
  icon: IconName;
  path: string;
  group: NavGroup;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  badgeSelector?: (s: RootState) => number;
};

const NAV: NavItem[] = [
  { id: "dashboard",   label: "Dashboard",          icon: "dashboard",   path: "/dashboard",          group: "overview" },
  { id: "users",       label: "Users",              icon: "users",       path: "/users",              group: "catalog",    badgeSelector: (s) => s.users.total },
  { id: "sims",        label: "SIM Cards",          icon: "sim",         path: "/sim-cards",          group: "catalog",    badgeSelector: (s) => s.simCards.simCards.length },
  { id: "imsi",        label: "IMSI Maps",          icon: "link",        path: "/imsi-maps",          group: "catalog",    badgeSelector: (s) => s.imsiMaps.imsiMaps.length },
  // vs_offer_numbers is one shared pool across every brand, not partitioned,
  // so the API serves it to superadmins only. Hide it rather than show a brand
  // admin a menu entry that can only ever answer 403.
  { id: "offers",      label: "Offer Numbers",      icon: "tag",         path: "/offer-numbers",      group: "catalog",    superAdminOnly: true },
  { id: "numbers",     label: "Numbers",            icon: "phone",       path: "/numbers",            group: "catalog",    badgeSelector: (s) => s.numbers.numbers.length },
  { id: "orders",      label: "eSIM Orders",        icon: "box",         path: "/esim-orders",        group: "catalog",    badgeSelector: (s) => s.esimOrders.esimOrders.length },
  { id: "consumption", label: "Consumption",        icon: "consumption", path: "/consumption",        group: "operations" },
  { id: "push",        label: "Push Notifications", icon: "bell",        path: "/push-notifications", group: "operations" },
  { id: "billing",     label: "Billing Stats",      icon: "arrowUp",     path: "/billing/stats",      group: "operations" },
  { id: "admin-users", label: "Admin Users",        icon: "shield",      path: "/admin-users",        group: "operations", superAdminOnly: true },
  // A brand admin sees only its own brand here, and manages its own API keys.
  { id: "brands",      label: "Brands",             icon: "tag",         path: "/brands",             group: "operations" },
  { id: "partner-sms", label: "Partner SMS",        icon: "send",        path: "/partner-sms",        group: "operations" },
  { id: "white-label", label: "White Label",        icon: "link",        path: "/white-label",        group: "operations", superAdminOnly: true },
];

const GROUP_LABEL: Record<NavGroup, string> = {
  overview: "Overview",
  catalog: "Catalog",
  operations: "Operations",
};

function decodeJwtPayload(token: string | null): { role?: string; username?: string; brand_slug?: string | null } | null {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1]))?.data ?? null;
  } catch {
    return null;
  }
}

type SidebarProps = {
  onClose?: () => void;
};

const NavRow: React.FC<{ item: NavItem; onClose?: () => void }> = ({ item, onClose }) => {
  const count = useSelector((s: RootState) => item.badgeSelector ? item.badgeSelector(s) : 0);
  return (
    <NavLink
      to={item.path}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      onClick={onClose}
    >
      <Icon name={item.icon} size={16} />
      <span>{item.label}</span>
      {item.badgeSelector && count > 0 && (
        <span className="nav-badge">{count.toLocaleString()}</span>
      )}
    </NavLink>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const brand = useSelector((s: RootState) => s.brand);
  const token = useSelector((s: RootState) => s.auth.token);

  const payload = decodeJwtPayload(token);
  const role = payload?.role ?? null;
  const jwtBrandSlug = payload?.brand_slug ?? null;
  const isAdmin = role === "admin";
  const isSuperAdmin = isAdmin && !jwtBrandSlug;
  const displayUsername = payload?.username ?? "Ops Admin";

  const handleLogout = () => {
    dispatch(clearToken());
    logoutUser();
    navigate("/login");
  };

  const groups: NavGroup[] = ["overview", "catalog", "operations"];

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt={brand.name}
            className="brand-logo"
            style={{ width: 26, height: 26, objectFit: "contain", borderRadius: 7 }}
          />
        ) : (
          <div
            className="brand-mark"
            style={{ background: "var(--color-primary)" }}
          >
            {brand.name.charAt(0).toLowerCase()}
          </div>
        )}
        <div>
          <div className="brand-name">{brand.name}</div>
          <div className="brand-sub">
            {isSuperAdmin ? "Super Admin · prod" : (jwtBrandSlug ? jwtBrandSlug + " · prod" : "prod")}
          </div>
        </div>
        <button className="sidebar-close" onClick={onClose} aria-label="Close menu">
          <Icon name="x" size={16} />
        </button>
      </div>

      {groups.map((group) => (
        <React.Fragment key={group}>
          <div className="nav-label">{GROUP_LABEL[group]}</div>
          {NAV.filter((n) => {
            if (n.group !== group) return false;
            if (n.superAdminOnly && !isSuperAdmin) return false;
            if (n.adminOnly && !isAdmin) return false;
            return true;
          }).map((item) => (
            <NavRow key={item.id} item={item} onClose={onClose} />
          ))}
        </React.Fragment>
      ))}

      <div className="sidebar-footer">
        <div className="avatar">{displayUsername.substring(0, 2).toUpperCase()}</div>
        <div className="who">
          <span className="n">{displayUsername}</span>
          <span className="e">{isSuperAdmin ? "Super Admin" : (jwtBrandSlug ? "Brand: " + jwtBrandSlug : "Admin")}</span>
        </div>
        <button className="icon-btn" onClick={handleLogout} title="Sign out" aria-label="Sign out">
          <Icon name="logout" />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
export { NAV };
